import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';

const formatMoney = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
};

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicacionLabel = (inv) => {
    const parts = [];
    if (inv?.area?.nombre) parts.push(inv.area.nombre);
    if (inv?.organizador?.nombre) parts.push(inv.organizador.nombre);
    if (inv?.ubicacion?.nombre) parts.push(inv.ubicacion.nombre);
    return parts.join(' / ');
};

const normalizarProveedores = (proveedores) =>
    (Array.isArray(proveedores) ? proveedores : []).map((p) => ({
        ...p,
        codigo_proveedor: p?.codigo ?? p?.codigo_proveedor ?? null
    }));

export default function SeleccionUbicacionesModal({ show, onHide, onConfirm, producto, esCotizacion, omitirUbicacion, moneda, tipoCambio }) {
    const inventarios = useMemo(
        () => (Array.isArray(producto?.inventarios) ? producto.inventarios : []),
        [producto]
    );
    const proveedores = useMemo(
        () => (Array.isArray(producto?.proveedores) ? producto.proveedores : []),
        [producto]
    );

    const hasStock = inventarios.length > 0;
    const modoSimplificado = hasStock && esCotizacion && !!omitirUbicacion;
    const sinProveedor = proveedores.length === 0;

    const stockTotal = useMemo(
        () => inventarios.reduce((acc, inv) => acc + Number(inv?.cantidad || 0), 0),
        [inventarios]
    );

    /* El precio de proveedor (catálogo) siempre está en MXN; se muestra convertido a la
       moneda activa de la venta, igual que en PreciosProveedorModal. */
    const esUsd = Number(moneda) === 1;
    const tc = esUsd && Number(tipoCambio) > 0 ? Number(tipoCambio) : 1;
    const prefMoneda = esUsd ? 'USD $' : '$';

    const proveedorOptions = useMemo(
        () =>
            proveedores.map((p, i) => ({
                value: i,
                label: `${p?.proveedor?.nombre || 'Proveedor'} · ${prefMoneda} ${formatMoney(Number(p?.precio_publico || 0) / tc)}`,
                raw: p
            })),
        [proveedores, prefMoneda, tc]
    );

    const [proveedorSel, setProveedorSel] = useState(null);
    const [asignaciones, setAsignaciones] = useState({});
    const [cantidadLibre, setCantidadLibre] = useState(0);
    const [precioLibre, setPrecioLibre] = useState(0);
    const [cantidadSimple, setCantidadSimple] = useState(0);

    const menuPortalTarget = useMemo(() => {
        if (typeof document === 'undefined') return null;
        return document.body;
    }, []);

    useEffect(() => {
        if (!show) return;

        setProveedorSel(proveedorOptions[0] || null);

        const init = {};
        inventarios.forEach((inv) => {
            init[inv._id] = 0;
        });
        setAsignaciones(init);

        setCantidadLibre(0);
        setPrecioLibre(0);
        setCantidadSimple(0);
    }, [show, inventarios, proveedorOptions]);

    const precioProveedor = Number(proveedorSel?.raw?.precio_publico || 0);

    /* Al elegir un proveedor, se sugiere su precio (ya convertido a la moneda activa) en el campo
       de precio libre — solo aplica cuando no hay inventario, que es donde ese campo es editable. */
    useEffect(() => {
        if (!show || hasStock) return;
        setPrecioLibre(Number((precioProveedor / tc).toFixed(3)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, hasStock, proveedorSel, tc]);

    const totalAsignado = useMemo(
        () => Object.values(asignaciones).reduce((acc, v) => acc + Number(v || 0), 0),
        [asignaciones]
    );

    const puedeConfirmar = modoSimplificado
        ? Number(cantidadSimple || 0) > 0
        : (hasStock ? totalAsignado > 0 : Number(cantidadLibre || 0) > 0);

    const onChangeAsignacion = (inv, rawValue) => {
        const v = rawValue === '' ? 0 : Number(rawValue);

        if (!esCotizacion) {
            const disponible = Number(inv?.cantidad || 0);
            const clamped = Math.max(0, Math.min(v, disponible));
            setAsignaciones((prev) => ({ ...prev, [inv._id]: clamped }));
            return;
        }

        setAsignaciones((prev) => ({ ...prev, [inv._id]: Math.max(0, v) }));
    };

    const handleConfirm = () => {
        const proveedoresNorm = normalizarProveedores(proveedores);

        if (modoSimplificado) {
            onConfirm({
                precioBase: sinProveedor ? Number(precioLibre || 0) : precioProveedor,
                precioEnMonedaActiva: sinProveedor,
                proveedoresNorm,
                selecciones: [
                    {
                        areaId: null,
                        organizadorId: null,
                        ubicacionId: null,
                        ubicacionLabel: null,
                        inventarioBase: stockTotal,
                        cantidad: Number(cantidadSimple || 0),
                        sinInventario: false
                    }
                ]
            });
            return;
        }

        if (hasStock) {
            const selecciones = inventarios
                .filter((inv) => Number(asignaciones[inv._id] || 0) > 0)
                .map((inv) => ({
                    areaId: inv?.area?._id || null,
                    organizadorId: inv?.organizador?._id || null,
                    ubicacionId: inv?.ubicacion?._id || null,
                    ubicacionLabel: buildUbicacionLabel(inv),
                    inventarioBase: Number(inv?.cantidad || 0),
                    cantidad: Number(asignaciones[inv._id]),
                    sinInventario: false
                }));

            onConfirm({
                precioBase: sinProveedor ? Number(precioLibre || 0) : precioProveedor,
                precioEnMonedaActiva: sinProveedor,
                proveedoresNorm,
                selecciones
            });
            return;
        }

        onConfirm({
            precioBase: Number(precioLibre || 0),
            precioEnMonedaActiva: true,
            proveedoresNorm,
            selecciones: [
                {
                    areaId: null,
                    organizadorId: null,
                    ubicacionId: null,
                    ubicacionLabel: null,
                    inventarioBase: 0,
                    cantidad: Number(cantidadLibre || 0),
                    sinInventario: true
                }
            ]
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Agregar producto</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ overflow: 'visible' }}>
                <div className="mb-3">
                    <div className="fw-semibold">{producto?.nombre || ''}</div>
                    {producto?.codigo_tienda ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>{producto.codigo_tienda}</div>
                    ) : null}
                </div>

                {proveedorOptions.length > 0 ? (
                    <div className="mb-3">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio de proveedor</div>
                        <Select
                            placeholder="Selecciona proveedor"
                            value={proveedorSel}
                            onChange={(opt) => setProveedorSel(opt)}
                            options={proveedorOptions}
                            isSearchable={false}
                            menuPortalTarget={menuPortalTarget}
                            menuPosition="fixed"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                ) : (hasStock || modoSimplificado) ? (
                    <div className="mb-3" style={{ maxWidth: 260 }}>
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                            Precio (este producto no tiene proveedores registrados)
                        </div>
                        <CurrencyInput
                            value={precioLibre}
                            decimalsLimit={3}
                            allowNegativeValue={false}
                            placeholder={`${prefMoneda} Precio`}
                            className="form-control"
                            prefix={`${prefMoneda} `}
                            groupSeparator=","
                            decimalSeparator="."
                            onValueChange={(value) => {
                                setPrecioLibre(value === undefined || value === null ? '' : value);
                            }}
                        />
                    </div>
                ) : null}

                {modoSimplificado ? (
                    <div>
                        <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                            La ubicación se definirá hasta que surtas la cotización. Solo indica la cantidad a cotizar.
                        </div>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="text-muted mb-1" style={{ fontSize: 12 }}>Cantidad</div>
                                <Form.Control
                                    type="number"
                                    value={cantidadSimple}
                                    min={0}
                                    placeholder="Cantidad"
                                    onChange={(e) => setCantidadSimple(e.target.value === '' ? 0 : Number(e.target.value))}
                                />
                            </div>
                            <div className="col-md-6">
                                <div className="text-muted mb-1" style={{ fontSize: 12 }}>Stock disponible (total)</div>
                                <Form.Control type="text" value={formatNumber(stockTotal)} disabled />
                            </div>
                        </div>
                    </div>
                ) : hasStock ? (
                    <div className="table-responsive">
                        <Table bordered hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Ubicación</th>
                                    <th style={{ width: 130 }} className="text-center">Disponible</th>
                                    <th style={{ width: 160 }} className="text-center">Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inventarios.map((inv) => (
                                    <tr key={inv._id}>
                                        <td className="align-middle">{buildUbicacionLabel(inv) || 'Sin ubicación'}</td>
                                        <td className="text-center align-middle">{formatNumber(inv?.cantidad)}</td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={asignaciones[inv._id] ?? 0}
                                                min={0}
                                                max={esCotizacion ? undefined : Number(inv?.cantidad || 0)}
                                                onChange={(e) => onChangeAsignacion(inv, e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div>
                        {esCotizacion ? (
                            <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                                Este producto no tiene existencia. Puedes cotizarlo indicando la cantidad y el precio.
                            </div>
                        ) : (
                            <div className="text-danger mb-3" style={{ fontSize: 13 }}>
                                Este producto no tiene existencia disponible.
                            </div>
                        )}

                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="text-muted mb-1" style={{ fontSize: 12 }}>Cantidad</div>
                                <Form.Control
                                    type="number"
                                    value={cantidadLibre}
                                    min={0}
                                    placeholder="Cantidad"
                                    disabled={!esCotizacion}
                                    onChange={(e) => setCantidadLibre(e.target.value === '' ? 0 : Number(e.target.value))}
                                />
                            </div>
                            <div className="col-md-6">
                                <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio</div>
                                <CurrencyInput
                                    value={precioLibre}
                                    decimalsLimit={3}
                                    allowNegativeValue={false}
                                    placeholder={`${prefMoneda} Precio`}
                                    className="form-control"
                                    prefix={`${prefMoneda} `}
                                    groupSeparator=","
                                    decimalSeparator="."
                                    disabled={!esCotizacion}
                                    onValueChange={(value) => {
                                        setPrecioLibre(value === undefined || value === null ? '' : value);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {hasStock && !modoSimplificado ? (
                    <div className="d-flex justify-content-end mt-3">
                        <Badge bg={totalAsignado > 0 ? 'success' : 'secondary'}>
                            Total a agregar: {formatNumber(totalAsignado)}
                        </Badge>
                    </div>
                ) : null}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirm} disabled={!puedeConfirmar}>
                    Agregar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
